import { availabilitySearchAll, availabilitySearchAllHome, availabilitySearchAllModify, availabilitySearchByQueryParams } from '@actions/availability.action';
import {
  bookingGetBookingAfterPassengerUpdateSuccess,
  bookingGetBookingSuccess,
  bookingPaymentsAccepted,
  bookingPaymentsAcceptedBookingUpdated,
  bookingPaymentsDeclined,
  bookingRetrieveBookingSuccess,
  checkCreditCardFeeSucceed,
  loadCreditCardFeesSucceed,
  resetCreditCardFee,
  resetCreditCardFeeSucceed,
} from '@actions/booking.action';
import {
  paymentCreditFileVerificationFailure,
  paymentCreditFileVerificationSuccess,
  paymentVoucherVerificationFailure,
  paymentVoucherVerificationSuccess,
  paymentUpdateMarketingOptIn,
  setUseMultiplePayments,
  paymentSelectFlow,
  setPaymentStatusReference, setPaymentInProgress,
} from '@actions/payment.action';
import { CurrencyKeyType, FALLBACK_CURRENCY } from '@constants/app.constants';
import { MAX_SINGLE_PAYMENT_AMOUNTS_BY_CURRENCY } from '@constants/form.constants';
import { NullableString } from '@models/app.models';
import { PaymentFlow } from '@models/payment.model';
import { N0CreditAccount } from '@models/responses/credit-file-information.model';
import {
  VoucherInformation,
  VoucherStatus,
} from '@models/responses/voucher-information.model';
import { Action, createReducer, on } from '@ngrx/store';
import {CreditCardFeeMap, FeeType} from "../../../typings/credit-card-type";

export interface State {
  creditCardFeeAmountApplied: number | string;
  creditCardFees: CreditCardFeeMap | null;
  creditFileInformation: {
    [voucherCode: string]: N0CreditAccount | undefined;
  };
  flow: PaymentFlow | null;
  isCorporateCreditCard: boolean;
  isUsingMultiplePayments: boolean;
  marketingOptIn: boolean;
  maxPaymentAmount: number;
  paymentInProgress: boolean;
  paymentStatusReference: NullableString;
  voucherVerificationInformation: {
    [voucherCode: string]: VoucherInformation | undefined;
  };
}

export const INITIAL_STATE: State = {
  isCorporateCreditCard: false,
  creditCardFeeAmountApplied: 0,
  creditCardFees: null,
  voucherVerificationInformation: {},
  creditFileInformation: {},
  marketingOptIn: false,
  maxPaymentAmount: MAX_SINGLE_PAYMENT_AMOUNTS_BY_CURRENCY[FALLBACK_CURRENCY],
  isUsingMultiplePayments: false,
  flow: null,
  paymentStatusReference: null,
  paymentInProgress: false,
};

const reducer = createReducer(
  INITIAL_STATE,

  // on modify, clear verifications
  on(
    bookingRetrieveBookingSuccess,
    (state): State => ({
      ...state,
      voucherVerificationInformation: {},
      creditFileInformation: {},
    }),
  ),

  on(
    paymentVoucherVerificationSuccess,
    (state, action): State => ({
      ...state,
      voucherVerificationInformation: {
        ...state.voucherVerificationInformation,
        [action.voucherInformation.reference]: action.voucherInformation,
      },
    }),
  ),

  on(
    paymentVoucherVerificationFailure,
    (state, action): State => ({
      ...state,
      voucherVerificationInformation: {
        ...state.voucherVerificationInformation,
        [action.voucherCode]: {
          status: VoucherStatus.Void,
        } as VoucherInformation,
      },
    }),
  ),

  on(
    paymentCreditFileVerificationSuccess,
    (state, action): State => ({
      ...state,
      creditFileInformation: {
        ...state.creditFileInformation,
        [action.creditFilePnr]: action.creditFileInformation,
      },
    }),
  ),

  on(
    paymentCreditFileVerificationFailure,
    (state, action): State => ({
      ...state,
      creditFileInformation: {
        ...state.creditFileInformation,
        [action.creditFilePnr]: undefined,
      },
    }),
  ),

  on(
    paymentUpdateMarketingOptIn,
    (state, action): State => ({
      ...state,
      marketingOptIn: action.enable,
    }),
  ),

  on(
    bookingPaymentsAccepted,
    (state): State => ({
      ...state,
      isUsingMultiplePayments: false,
      paymentStatusReference: null
    }),
  ),
  on(
    bookingPaymentsDeclined,
    (state): State => ({
      ...state,
      paymentStatusReference: null
    }),
  ),
  // set max payment amount
  on(
    bookingGetBookingSuccess,
    bookingPaymentsAcceptedBookingUpdated,
    bookingGetBookingAfterPassengerUpdateSuccess,
    (state, { booking: { currencyCode } }): State => {
      const currency = (currencyCode ?? FALLBACK_CURRENCY) as CurrencyKeyType;
      return {
        ...state,
        maxPaymentAmount: MAX_SINGLE_PAYMENT_AMOUNTS_BY_CURRENCY[currency],
      };
    },
  ),

  // set multiple payments flag initial value from balance due and max payment amount
  on(
    bookingGetBookingSuccess,
    bookingGetBookingAfterPassengerUpdateSuccess,
    bookingPaymentsAcceptedBookingUpdated,
    (state, action): State => ({
      ...state,
      isUsingMultiplePayments:
        (action.booking.breakdown?.balanceDue ?? 0) > state.maxPaymentAmount,
    }),
  ),

  on(
    setUseMultiplePayments,
    (state, action): State => ({
      ...state,
      isUsingMultiplePayments: action.value,
    }),
  ),

  on(paymentSelectFlow,
    (state, action): State => ({
      ...state,
      flow: action.flow
    }),
  ),
  on(setPaymentInProgress,
    (state, action): State => ({
      ...state,
      paymentInProgress: action.paymentInProgress
    }),
  ),
  on(setPaymentStatusReference, (state, action): State =>({
    ...state,
    paymentStatusReference: action.paymentStatusReference
  }))
  ,
  on(
    availabilitySearchAllHome,
    availabilitySearchAllModify,
    availabilitySearchAll,
    availabilitySearchByQueryParams,
    (state): State =>({
    ...state,
    paymentStatusReference: null
  })),

  on(
    loadCreditCardFeesSucceed,
    (state, action): State =>({
    ...state,
    creditCardFees: action.fees
  })),

  on(
    resetCreditCardFee,
    (state): State =>({
    ...state,
      isCorporateCreditCard: false,
      creditCardFeeAmountApplied: 0,
  })),

  on(
    checkCreditCardFeeSucceed,
    (state, action): State =>({
    ...state,
      isCorporateCreditCard: action.response ? action.response.feeType === FeeType.CORPORATE : false,
      creditCardFeeAmountApplied: action.response ? action.response.feeAmount : 0
  })),

  on(
    resetCreditCardFeeSucceed,
    (state): State =>({
    ...state,
      isCorporateCreditCard: false,
      creditCardFeeAmountApplied: 0
  })),
);

export const paymentReducer = (state: State, action: Action): State =>
  reducer(state, action);
